/**
 * A seed row that points at a seed row of another list.
 *
 * A Sub-categories list is no use seeded without its Category: "Electricity"
 * belongs under "Utilities". A seed row names that parent the only way a spec
 * can, by the WORD its title holds, because a seeded row has no id until apply
 * mints one. So a `reference` value in a seed row is a catalogue key whose word
 * is the title of one of the target list's own `seedRows`, compared the way a
 * person would say it (case and outer spaces aside).
 *
 * The validator proves every such word names a row, and apply turns each into
 * the id it minted for that row. Both read the two helpers here, so "the same
 * word" and "which list is seeded first" mean one thing on both sides.
 */
import type { TemplateRecordSpec } from './spec';

/** Two seed words name the same row: case and outer spaces aside. */
export const seedWordsMatch = (a: string, b: string): boolean => a.trim().toLowerCase() === b.trim().toLowerCase();

/** The field key a record's rows are named by, from its `title` binding. */
export const titleFieldKeyOf = (record: TemplateRecordSpec): string | undefined =>
  (Array.isArray(record.bindings) ? record.bindings : []).find((binding) => binding.purpose === 'title')?.fieldKey;

/** Every seed row of a record, the always rows and the conditional ones. */
const allSeedValues = (record: TemplateRecordSpec): ReadonlyArray<Record<string, string | number>> => [
  ...(Array.isArray(record.seedRows) ? record.seedRows : []),
  ...(Array.isArray(record.seedRowsWhen) ? record.seedRowsWhen : []).map((row) => row?.values ?? {}),
];

/** The lists one record's seed rows point into: a reference field the rows fill. */
export const seedTargetsOf = (record: TemplateRecordSpec): string[] => {
  const fields = Array.isArray(record.fields) ? record.fields : [];
  const out = new Set<string>();
  for (const row of allSeedValues(record)) {
    for (const key of Object.keys(row ?? {})) {
      const field = fields.find((one) => one?.key === key);
      if (field?.type === 'reference' && field.targetRecordKey !== undefined && field.targetRecordKey !== record.key) {
        out.add(field.targetRecordKey);
      }
    }
  }
  return [...out];
};

/**
 * The records in the order to seed them: a list after every list its seed rows
 * point into, and otherwise in spec order. Null when two lists' seed rows point
 * into each other in a ring, which no order can seed.
 */
export const seedOrder = (records: readonly TemplateRecordSpec[]): TemplateRecordSpec[] | null => {
  const byKey = new Map(records.map((record) => [record.key, record]));
  const done = new Set<string>();
  const walking = new Set<string>();
  const out: TemplateRecordSpec[] = [];
  const visit = (record: TemplateRecordSpec): boolean => {
    if (done.has(record.key)) return true;
    if (walking.has(record.key)) return false;
    walking.add(record.key);
    for (const key of seedTargetsOf(record)) {
      const target = byKey.get(key);
      if (target !== undefined && !visit(target)) return false;
    }
    walking.delete(record.key);
    done.add(record.key);
    out.push(record);
    return true;
  };
  for (const record of records) {
    if (!visit(record)) return null;
  }
  return out;
};
